import { useState, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface TabSpec {
  id: string;
  label: string;
  content: ReactNode;
  count?: number;
}

/**
 * Simple controlled-by-state tabs. Active tab gets a 2px brand bottom border;
 * inactive tabs use muted text + transparent border that pales to zinc-300 on hover.
 */
export function Tabs({
  tabs,
  defaultTab,
  className,
}: {
  tabs: TabSpec[];
  defaultTab?: string;
  className?: string;
}) {
  const [active, setActive] = useState(defaultTab ?? tabs[0]?.id ?? '');

  return (
    <div className={className}>
      <div
        role="tablist"
        className="border-b border-zinc-200 dark:border-zinc-700 flex gap-6 -mx-6 px-6 overflow-x-auto nice-scroll"
      >
        {tabs.map((t) => {
          const isActive = t.id === active;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={isActive}
              onClick={() => setActive(t.id)}
              className={cn(
                'py-3 -mb-px border-b-2 text-sm font-medium transition flex items-center gap-2 whitespace-nowrap',
                isActive
                  ? 'border-brand text-zinc-950 dark:border-brand-dm dark:text-zinc-50'
                  : 'border-transparent text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:border-zinc-300 dark:hover:border-zinc-700',
              )}
            >
              {t.label}
              {t.count !== undefined && (
                <span
                  className={cn(
                    'px-1.5 py-px rounded text-[10px] font-medium tabular-nums',
                    isActive
                      ? 'bg-brand-subtle text-brand dark:bg-brand-dm-subtle dark:text-brand-dm'
                      : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300',
                  )}
                >
                  {t.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div className="pt-6">{tabs.find((t) => t.id === active)?.content}</div>
    </div>
  );
}
